/**
 * SRES · Step 5 · Logs & Maps (Feb 2026 · Iter82).
 *
 * Combines the two workflows the client mandate collapses into a
 * single step:
 *   • Continuous drillhole logs         — BoreholeLogWorkspace
 *   • GIS boundary & map gallery        — GISStudio
 *
 * The two full-page views are preserved as-is (no logic changes) and
 * shown behind a top toggle. This lets us keep every existing test-id
 * and behaviour intact while satisfying the "one step, no duplicate
 * menus" requirement.
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Compass, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import BoreholeLogWorkspace from "@/pages/logs/BoreholeLogWorkspace";
import GISStudio from "@/pages/gis/GISStudio";
import FlowNav from "@/components/FlowNav";

const TABS = [
  { key: "logs", label: "Drillhole Logs", Icon: Compass },
  { key: "maps", label: "GIS Maps",       Icon: MapTrifold },
];

export default function LogsMapsPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("logs");

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50"
              data-testid="logs-maps-page">
      <header className="shrink-0 bg-white border-b border-slate-200 z-10">
        <div className="px-6 py-3">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
            Stage 5 of 9 · Logs &amp; Maps
          </div>
          <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none">
            Logs &amp; Maps
          </h1>
          <p className="text-[12px] text-slate-500 mt-1">
            Review continuous drillhole logs and inspect the GIS boundary,
            map gallery, borehole validation, and exports for this project.
          </p>
        </div>
        <nav className="px-6 flex gap-1 border-t border-slate-100 bg-slate-50/50"
                data-testid="logs-maps-tabs">
          {TABS.map((t) => (
            <button key={t.key}
                       onClick={() => setTab(t.key)}
                       data-testid={`logs-maps-tab-${t.key}`}
                       aria-current={tab === t.key ? "page" : undefined}
                       className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold uppercase tracking-widest border-b-2 transition
                          ${tab === t.key
                            ? "border-brand-blue text-brand-blue"
                            : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.Icon size={12} weight="duotone" /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden flex flex-col"
                data-testid="logs-maps-body">
        {tab === "logs" ? (
          <div className="flex-1 min-h-0" data-testid="logs-maps-logs-panel">
            <BoreholeLogWorkspace />
          </div>
        ) : (
          <div className="flex-1 min-h-0" data-testid="logs-maps-maps-panel">
            <GISStudio />
          </div>
        )}
      </main>

      <FlowNav
        pid={pid}
        hideCancel
        hideNext
        onSave={() => toast.success("Stage 5 · Logs & Maps saved")}
        onSaveContinue={() => nav(`/app/projects/${pid}/wizard/estimation_params`)}
      />
    </div>
  );
}
